import { components } from '@invo.space/shared'

// Hand-written types for the eshop admin API (/api/v2).
// TODO: replace with generated `components['schemas'][...]` once the
// feat/eshop swagger is deployed and `make generate-schema` is re-run.

export const deliveryMethodTypes = [
  'personalPickup',
  'ceskaPosta',
  'balikovna',
  'ppl',
  'zasilkovna',
] as const

export type DeliveryMethodType = (typeof deliveryMethodTypes)[number]

export const zasilkovnaPickupSelectors = ['widget', 'feed'] as const

export type ZasilkovnaPickupSelector =
  (typeof zasilkovnaPickupSelectors)[number]

export const balikovnaPickupSelectors = ['widget', 'feed'] as const

export type BalikovnaPickupSelector =
  (typeof balikovnaPickupSelectors)[number]

export const paymentMethodTypes = [
  'payOnPickup',
  'cashOnDelivery',
  'bankTransfer',
  'cardPayment',
] as const

export type PaymentMethodType = (typeof paymentMethodTypes)[number]

export const productTypes = ['physical', 'virtual', 'service', 'package'] as const

export type ProductType = (typeof productTypes)[number]

export const cartPriceModes = ['live', 'snapshot'] as const

export type CartPriceMode = (typeof cartPriceModes)[number]

export const addressRequirements = ['always', 'whenAddressDelivery', 'never'] as const

export type AddressRequirement = (typeof addressRequirements)[number]

export type EshopMoney = {
  amount: string
  currency: string
}

export type EshopMethodPrice = {
  price: EshopMoney
  freeFromThreshold: EshopMoney | null
}

export type DeliveryMethod = {
  type: DeliveryMethodType
  isEnabled: boolean
  supportedProductTypes: ProductType[]
  prices: EshopMethodPrice[]
  allowedPaymentMethods: PaymentMethodType[]
  zasilkovnaPickupSelector: ZasilkovnaPickupSelector | null
  zasilkovnaApiKey: string | null
  balikovnaPickupSelector: BalikovnaPickupSelector | null
}

export type EshopPaymentMethod = {
  type: PaymentMethodType
  isEnabled: boolean
  prices: EshopMethodPrice[]
  invoicingBankAccountId: string | null
  cardProviderKey: string | null
}

export type PickupPoint = {
  id: string
  name: string
  street: string
  city: string
  zip: string
  country: string
  note: string | null
  openingHours: string | null
  isActive: boolean
}

export type StorefrontToken = {
  id: string
  name: string
  createdAt: string
  revokedAt: string | null
}

export type StorefrontTokenCreated = {
  id: string
  name: string
  token: string
}

// --- Storefront API (/api/storefront/v1) response types ---
// Mirror the backend V2 response mappers reused by the storefront controllers.

export type StorefrontProduct = {
  id: string
  productType: ProductType
  name: string
  slug: string
  description: string | null
  mainCategoryId: string | null
  secondaryCategoryIds: string[]
  externalId: string | null
  isActive: boolean
  parameters: NonNullable<components['schemas']['Product']['parameters']>
  variantAxes: components['schemas']['Product']['variantAxes']
  createdAt: string | null
}

export type StorefrontCategory = {
  id: string
  name: string
  materializedName: string
  slug: string
  parentId: string | null
  externalId: string | null
  position: number
  createdAt: string | null
  children?: StorefrontCategory[]
  products?: StorefrontProduct[]
}

export type StorefrontSkuPrice = {
  price: EshopMoney
  validFrom: string | null
  validTo: string | null
  note: string | null
  taxRate?: {
    id: number
    name: string
    percentage: number
  }
}

export type StorefrontSku = {
  id: string
  name: string
  slug: string
  code: string | null
  productId: string
  isBundle: boolean
  isActive: boolean
  isSystem: boolean
  optionValueIds: components['schemas']['Sku']['optionValueIds']
  gallery: components['schemas']['SkuImage'][]
  price: StorefrontSkuPrice | null
  createdAt: string | null
}

export type CartAddress = {
  street: string | null
  city: string | null
  zip: string | null
  country: string | null
}

export type CartCustomer = {
  email: string | null
  phone: string | null
  name: string | null
  billingAddress: CartAddress
  shippingAddress: CartAddress
}

export type CartItem = {
  id: string
  type: string
  skuId: string | null
  deliveryMethodType: string | null
  pickupPointId: string | null
  carrierPickupPointId: string | null
  paymentMethodType: string | null
  quantity: number
  name: string | null
  unitPrice: EshopMoney | null
  unitPriceInclVat?: EshopMoney | null
  taxRate: number | null
  lineExtras: components['schemas']['DocumentItemLineExtra'][] | null
}

export type CartTotals = {
  productsSubtotalExclVat: EshopMoney
  productsSubtotalInclVat: EshopMoney
  deliveryFee: EshopMoney | null
  paymentFee: EshopMoney | null
  totalInclVat: EshopMoney
}

export type Cart = {
  id: string
  status: string
  currency: string
  customer: CartCustomer
  items: CartItem[]
  totals: CartTotals
  promotedOrderId: string | null
  lastActivityAt: string
}

export type CreatedCart = Cart & {
  secretToken: string
}

export type CarrierPickupPoint = {
  id: string
  provider: string
  externalId: string
  name: string
  street: string | null
  city: string | null
  zip: string | null
  country: string | null
  latitude: string | number | null
  longitude: string | number | null
  distanceKm?: number
}

export type PromotedOrder = {
  orderId: number
  orderUuid: string
  symbol: string
}

// Mirrors backend DeliveryPaymentMethodValidator: payOnPickup only with
// personalPickup, cashOnDelivery only with carrier delivery methods.
export function allowedPaymentMethodsForDelivery(
  type: DeliveryMethodType,
): PaymentMethodType[] {
  if (type === 'personalPickup') {
    return ['payOnPickup', 'bankTransfer', 'cardPayment']
  }
  return ['cashOnDelivery', 'bankTransfer', 'cardPayment']
}
